import { box, type World, type Aabb } from '../sim.js';
import { PLAYER_RADIUS } from '../constants.js';
import type { Vec3 } from '../math.js';
import { EntityKind } from '../protocol.js';
import type {
  AreaDef,
  MapDef,
  LootContainerDef,
  ExtractDef,
  HymnSpawnDef,
  NpcDef,
  WaypointGraph,
} from '../maps.js';
import type { VoiceId } from '../quests.js';
import { buildHeightfield, heightAt, type Heightfield } from '../terrain.js';
import { slabRoom, stairs, pillarRow, scatter, groundHeightAt, type Rect } from './kit.js';
import { SPINE_GRID, type SpineRegion } from './region.js';
// The in-raid map's geometry (maps/zonemap.ts) is plain data with no DOM in it, and the rects
// below are the SAME rects the map draws its deck plan from - so they are authored there and
// imported here, rather than written twice and left to drift.
import {
  SPINE_APRON_RECT,
  SPINE_CHAIN_RECT,
  SPINE_PIT_RECT,
  SPINE_RIDGE_HATCH_RECT,
  SPINE_SHAFT_RECT,
  SPINE_STAIR_RECT,
} from './zonemap.js';

/**
 * The Spine v2 - a ~264 x 168 m section of the generation ship *Candle*, tilted 12 degrees (faked
 * by stepping three decks in Y - boxes stay axis-aligned) and half-swallowed by dunes, under the
 * Band's eternal dusk. X is east, Z is south, y is up, yaw 0 faces -Z (north). The hull's three
 * decks share one 50 x 28 m footprint (x -25..25, z 6..34) stacked directly on top of each other;
 * each deck's own floor doubles as the ceiling for the deck below it (no separate roof slab, so
 * two flush slabs never overlap), except the topmost deck (Choir Deck), whose roof *is* the
 * dorsal ridge walking surface.
 *
 * Decks (see the exported *_BASE/_H constants for exact numbers):
 *  - Deck 0, Cargo Hold (y 0-8): container stacks, a "black drop" pit under two catwalks, the
 *    Cargo Lift extract in a north alcove, a south breach onto the approach dunes.
 *  - Deck 1, Habitation Ring (y 8-14): narrow berths; reached from Deck 0 by a collapsed-floor
 *    mantle chain (1.14 m risers, `MANTLE_MAX_HEIGHT` in `constants.ts` is 1.4) and, separately,
 *    a walkable stair (0.4 m risers, inside `STEP_UP`); an east breach reached from an exterior
 *    dune crest by jump + mantle.
 *  - Deck 2, Choir Deck (y 14-20.4): the Verger arena (pillars, anchor pads); reached from Deck 1
 *    by a lift shaft (mantle ledges on one side, an open drop on the other - the drop side is open
 *    the full 14 m down to Deck 0's floor, so jumping down it is a real fall-damage choice); an
 *    internal stair (16 x 0.4 m) climbs through a roof hatch onto the roof - the dorsal ridge.
 *  - Dorsal ridge (roof of Deck 2, y ~20.4): open to the sky, a stepped gantry (16 x 0.4 m risers
 *    on 4.125 m treads) runs north from the hull to the natural 14 m terrain ridge, where the Rail
 *    Car extract sits.
 *
 * Riser policy: every route a player is expected to *run* is built from risers inside `STEP_UP`
 * (0.5 m) so `stepPlayer` walks them; mantle-height risers (0.5-1.4 m) are reserved for the two
 * deliberate skill routes - the Deck 0 -> Deck 1 collapsed-floor chain and the lift-shaft ledges -
 * plus the exterior dune crest. Mixing the two on a running route is what produced the
 * "stairs are super glitchy, screen freaks out" playtest report on 2026-09-04.
 *
 * Terrain: a 4 m-cell heightfield covers the whole map - rolling regolith dunes (0-5 m, a
 * deterministic sum of sines), rising to the 14 m north ridge (centred z=-60), flattened to a flat
 * 0-height apron under and around the hull footprint so its interiors sit on y=0 floors. Every
 * content position is computed from this geometry (`groundHeightAt`/`heightAt`), never hardcoded.
 *
 * Region sidecar: the in-code heightfield above is the FALLBACK. The shipped exterior is sculpted
 * in Blender (`tools/assetgen/blender/spine_region.py`), which exports the visuals as a GLB and a
 * `spine_region.json` sidecar carrying the same terrain sampled on this exact 4 m grid plus an AABB
 * for every landmark it placed. `buildSpine({ region })` authors the whole map against that
 * heightfield instead (every container/spawn/waypoint position is re-derived from it) and swaps the
 * in-code scree scatter for the region's colliders; `applySpineRegion()` does that in place on the
 * `SPINE` singleton so every existing import of it - `MAPS`, the server's rooms, the bots' nav
 * cache - sees the same map. Both ends must load it, or neither (see region.ts).
 */

// ---------------------------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------------------------

/** Deterministic rolling dunes, 0-5 m: a fixed sum of sines (no RNG, so it's reproducible). */
function duneNoise(x: number, z: number): number {
  const h =
    2.4 +
    1.6 * Math.sin(x * 0.045 + z * 0.03) +
    1.1 * Math.sin(x * 0.02 - z * 0.07 + 1.3) +
    0.6 * Math.sin(x * 0.09 + z * 0.11 + 2.7);
  return Math.min(5, Math.max(0, h));
}

/** The 14 m ridge along the north edge (z ~ -60), a smooth bump falling off over ~22 m. */
function ridgeHeight(z: number): number {
  const d = z + 60;
  return 14 * Math.exp(-(d * d) / (2 * 22 * 22));
}

/** Rect the hull's decks sit over, flattened to y=0 (plus the dune-crest apron east of it). */
const APRON: Rect = SPINE_APRON_RECT;
const APRON_MARGIN = 14;

function apronMask(x: number, z: number): number {
  const dx = x < APRON.x0 ? APRON.x0 - x : x > APRON.x1 ? x - APRON.x1 : 0;
  const dz = z < APRON.z0 ? APRON.z0 - z : z > APRON.z1 ? z - APRON.z1 : 0;
  const d = Math.hypot(dx, dz);
  return Math.max(0, 1 - d / APRON_MARGIN);
}

function terrainHeight(x: number, z: number): number {
  const raw = Math.max(duneNoise(x, z), ridgeHeight(z));
  return raw * (1 - apronMask(x, z));
}

/** The in-code terrain, used whenever no region sidecar has been applied (see the header). */
const FALLBACK_TERRAIN: Heightfield = buildHeightfield(
  SPINE_GRID.originX,
  SPINE_GRID.originZ,
  SPINE_GRID.cellSize,
  SPINE_GRID.cols,
  SPINE_GRID.rows,
  terrainHeight,
);

// ---------------------------------------------------------------------------------------------
// Deck geometry
// ---------------------------------------------------------------------------------------------

/** Shared footprint for all three decks. */
const HULL_X0 = -25;
const HULL_X1 = 25;
const HULL_Z0 = 6;
const HULL_Z1 = 34;
const HULL_CX = (HULL_X0 + HULL_X1) / 2;
const HULL_CZ = (HULL_Z0 + HULL_Z1) / 2;
const HULL_W = HULL_X1 - HULL_X0;
const HULL_D = HULL_Z1 - HULL_Z0;
const WALL_T = 0.4;

export const DECK0_H = 8;
export const DECK1_H = 6;
export const DECK2_H = 6;
export const DECK0_BASE = 0;
export const DECK1_BASE = DECK0_BASE + DECK0_H; // 8 - Deck 1's floor is Deck 0's ceiling.
export const DECK2_BASE = DECK1_BASE + DECK1_H; // 14 - Deck 2's floor is Deck 1's ceiling.
/** Top of Deck 2's roof slab - the dorsal ridge walking surface. */
export const RIDGE_Y = DECK2_BASE + DECK2_H + WALL_T; // 20.4

/** Verger arena centre, for `spawnVerger`/`debugSpawnVerger` (not part of `MapDef` - see the
 *  report). Sits on Deck 2's floor, clear of the pillars, pads, shaft and the ridge chain. */
export const SPINE_VERGER_SPAWN: Vec3 = { x: 0, y: DECK2_BASE, z: 20 };

export interface SpineBuildOptions {
  /** A parsed region sidecar (region.ts). Omit for the in-code fallback terrain. */
  region?: SpineRegion;
}

/**
 * Authors the whole map against `opts.region`'s heightfield, or the in-code one. Pure: two calls
 * with the same options produce equal maps. `SPINE` below is the module-level singleton every
 * consumer imports; `applySpineRegion` rebuilds it in place.
 */
export function buildSpine(opts: SpineBuildOptions = {}): MapDef {
  const TERRAIN: Heightfield = opts.region?.heightfield ?? FALLBACK_TERRAIN;
  const boxes: Aabb[] = [];

  /** Black-drop pit under Deck 0's floor: a hole to a floor slab 6 m down. */
  const PIT_RECT: Rect = SPINE_PIT_RECT;
  const PIT_FLOOR_Y = DECK0_BASE - 6;
  /** Deck 0 -> Deck 1 collapsed-floor mantle chain (west side). */
  const CHAIN_RECT: Rect = SPINE_CHAIN_RECT;
  /** Deck 0 -> Deck 1 walkable stair, a second route (west side, south of the chain). */
  const STAIR_RECT: Rect = SPINE_STAIR_RECT;
  /** Deck 1 -> Deck 2 lift shaft: mantle ledges on the north half, an open drop on the south half -
   *  the drop punches through both Deck 1's and Deck 2's floors, so it falls all the way to Deck 0. */
  const SHAFT_RECT: Rect = SPINE_SHAFT_RECT;
  /** Deck 2 roof hatch above the internal ridge stair: the stair's last treads climb above the
   *  roof's underside (y=20), so the roof needs an actual opening there. Its last tread sits flush
   *  with RIDGE_Y, level with the solid roof just outside the hatch. Only treads whose feet are
   *  above y=18.2 need the hole (below that a standing capsule clears the roof underside), which
   *  the stair's run reaches at z ~10.2 - comfortably inside the hatch's z 6..14. */
  const RIDGE_HATCH_RECT: Rect = SPINE_RIDGE_HATCH_RECT;

  // --- Deck 0: Cargo Hold ---------------------------------------------------------------------
  boxes.push(
    ...slabRoom(
      HULL_CX,
      HULL_CZ,
      HULL_W,
      HULL_D,
      DECK0_H,
      WALL_T,
      [
        { wall: 's', center: 0, width: 5 }, // south breach onto the approach dunes
        { wall: 'n', center: -18, width: 3 }, // north alcove, Cargo Lift extract
      ],
      { yBase: DECK0_BASE, roof: false, floorHoles: [PIT_RECT] },
    ),
  );
  boxes.push(box(0, PIT_FLOOR_Y - 0.25, 20, 12, 0.5, 10)); // pit floor slab, top flush at y=-6
  // Two catwalks over the pit at y=4 (a 4 m fall to the catwalk, 6 m below that to the pit floor).
  boxes.push(box(0, 3.85, 17, 14, 0.3, 1.6)); // top flush y=4
  boxes.push(box(0, 3.85, 23, 14, 0.3, 1.6));

  // --- Deck 0 -> Deck 1 connectors -------------------------------------------------------------
  const CHAIN = stairs(
    CHAIN_RECT.x0,
    (CHAIN_RECT.z0 + CHAIN_RECT.z1) / 2,
    { x: 1, z: 0 },
    7,
    DECK1_BASE / 7,
    8 / 7,
    6,
    DECK0_BASE,
  );
  boxes.push(...CHAIN.boxes);
  const STAIR01 = stairs(
    (STAIR_RECT.x0 + STAIR_RECT.x1) / 2,
    STAIR_RECT.z0,
    { x: 0, z: 1 },
    20,
    0.4,
    0.6,
    6,
    DECK0_BASE,
  );
  boxes.push(...STAIR01.boxes);

  // --- Deck 1: Habitation Ring -------------------------------------------------------------------
  boxes.push(
    ...slabRoom(
      HULL_CX,
      HULL_CZ,
      HULL_W,
      HULL_D,
      DECK1_H,
      WALL_T,
      [{ wall: 'e', center: 14, width: 8 }], // east breach, reached from the exterior dune crest
      { yBase: DECK1_BASE, roof: false, floorHoles: [CHAIN_RECT, STAIR_RECT, SHAFT_RECT] },
    ),
  );
  // Berth-alcove partitions (flavour, clear of the connector holes and the shaft).
  const DECK1_MID_Y = DECK1_BASE + DECK1_H / 2;
  for (const px of [-10, -2, 6]) boxes.push(box(px, DECK1_MID_Y, 30, 0.2, DECK1_H, 6));

  // --- Exterior dune crest (Deck 1's east breach approach: jump + mantle). ----------------------
  const DUNE_CREST = stairs(32, 14, { x: -1, z: 0 }, 8, 1.0, 1.0, 8, 0);
  boxes.push(...DUNE_CREST.boxes);

  // --- Deck 1 -> Deck 2 lift shaft: mantle ledges (north half of SHAFT_RECT). ---------------------
  const SHAFT_LEDGES = stairs(
    SHAFT_RECT.x0,
    17,
    { x: 1, z: 0 },
    5,
    (DECK2_BASE - DECK1_BASE) / 5,
    8 / 5,
    2,
    DECK1_BASE,
  );
  boxes.push(...SHAFT_LEDGES.boxes);
  // SHAFT_RECT z 18..24 is left open (no boxes) for a straight fall from Deck 2 to Deck 0.

  // --- Deck 2: Choir Deck / Verger arena ---------------------------------------------------------
  boxes.push(
    ...slabRoom(HULL_CX, HULL_CZ, HULL_W, HULL_D, DECK2_H, WALL_T, [], {
      yBase: DECK2_BASE,
      roof: true,
      floorHoles: [SHAFT_RECT],
      roofHoles: [RIDGE_HATCH_RECT],
    }),
  );
  const PILLARS = pillarRow([-14, 14], [10, 18, 26], DECK2_H, 0.8, DECK2_BASE);
  boxes.push(...PILLARS);
  // Four anchor pads (low plinths) around the arena centre. (0,31.5) and (-14,21.5), not (0,30) and
  // (-14,20): those sat exactly on the deck2Row30 x=0 waypoint node and directly on the deck2Row20
  // z=20 travel line. A node standing *on* a pad reads its own 0.2 m lip as the surface beneath it,
  // and back when stepPlayer had no step-up the lip was an outright wall (offline traversal harness
  // below). The lip is walkable now, but the pads stay nudged off those exact lines: it keeps the
  // arena layout and leaves both travel lines flat.
  const ANCHOR_PADS: [number, number][] = [
    [0, 10],
    [0, 31.5],
    [-14, 21.5],
    [14, 20],
  ];
  for (const [ax, az] of ANCHOR_PADS) boxes.push(box(ax, DECK2_BASE + 0.1, az, 1.5, 0.2, 1.5));

  // --- Internal stair from the arena floor up onto the roof (the dorsal ridge). -------------------
  // 16 x 0.4 m risers on 0.7 m treads, not the old 5 x 1.28 m: at 1.28 every step was a full
  // auto-mantle, so running this climb was a burst of five snap-teleports ("the screen freaks out,
  // head bobs crazy" - owner playtest, 2026-09-04). 0.4 is inside sim.ts's STEP_UP, so the whole
  // flight is walked, not climbed. It ends flush with RIDGE_Y on the roof plane and starts at
  // z = RIDGE_HATCH_RECT.z0 + 16 * 0.8 = 18.8 on the arena floor - the run had to grow south to
  // buy the extra treads, which is free: the hatch itself is unchanged (see RIDGE_HATCH_RECT) and
  // the lower treads sit under solid roof with head room to spare.
  // Tread depth 0.8, not 0.7: a tread exactly one capsule diameter (2 * PLAYER_RADIUS) deep puts a
  // waypoint node at its centre precisely on the neighbouring tread's boundary, where floating
  // point decides whether the AI capsule "clips" it.
  const RIDGE_STAIR_STEPS = 16;
  const RIDGE_STAIR_DEPTH = 0.8;
  const RIDGE_STAIR = stairs(
    0,
    RIDGE_HATCH_RECT.z0 + RIDGE_STAIR_STEPS * RIDGE_STAIR_DEPTH,
    { x: 0, z: -1 },
    RIDGE_STAIR_STEPS,
    (RIDGE_Y - DECK2_BASE) / RIDGE_STAIR_STEPS, // 0.4
    RIDGE_STAIR_DEPTH,
    6,
    DECK2_BASE,
  );
  boxes.push(...RIDGE_STAIR.boxes);

  // --- Dorsal ridge: a stepped gantry from the hull's north wall out to the terrain ridge. --------
  // Same story as the ridge stair: 16 x 0.4 m risers instead of 5 x 1.28 m. The footprint is
  // untouched - it still starts on the 14 m terrain ridge at z=-60 (under the Rail Car) and ends
  // flush with the roof plane at z=HULL_Z0 - so the treads are just cut sixteen ways instead of
  // five: 66 m of run / 16 = 4.125 m per tread, a broad slab gantry you can actually run up.
  const DORSAL_Z0 = -60;
  const DORSAL_STEPS = 16;
  const DORSAL_RIDGE = stairs(
    0,
    DORSAL_Z0,
    { x: 0, z: 1 },
    DORSAL_STEPS,
    (RIDGE_Y - 14) / DORSAL_STEPS, // 0.4
    (HULL_Z0 - DORSAL_Z0) / DORSAL_STEPS, // 4.125
    10,
    14,
  );
  boxes.push(...DORSAL_RIDGE.boxes);

  // --- Scree cover on the dunes. -------------------------------------------------------------------
  // Generated here (so groundHeightAt placements below see the right terrain shape) but not pushed
  // into `boxes` until after the waypoint graph is built further down - see the clutter-vs-edge
  // filter there for why.
  const SCREE_COVER = scatter(
    1,
    18,
    { x0: -110, z0: -70, x1: 110, z1: 70 },
    [
      { w: 1, d: 1, hMin: 1, hMax: 1.2 },
      { w: 1.5, d: 1.2, hMin: 1.3, hMax: 1.6 },
      { w: 2, d: 1.5, hMin: 1.6, hMax: 2 },
    ],
    (x, z) => heightAt(TERRAIN, x, z),
  );
  // A little cover near the Antenna knoll specifically.
  const ANTENNA_COVER = scatter(
    2,
    5,
    { x0: 68, z0: 6, x1: 88, z1: 24 },
    [{ w: 1.2, d: 1.2, hMin: 1.4, hMax: 2.4 }],
    (x, z) => heightAt(TERRAIN, x, z),
  );

  const world: World = { groundY: 0, boxes, terrain: TERRAIN };

  // ---------------------------------------------------------------------------------------------
  // Ground-height helpers (cap `maxY` so a query at (x, z) doesn't pick up a higher deck's floor -
  // every deck shares the hull footprint, so without a cap a Deck 0 point would resolve to Deck 2's
  // roof, the tallest box at that (x, z)).
  // ---------------------------------------------------------------------------------------------

  const at = (x: number, z: number, maxY = Infinity): Vec3 => ({
    x,
    y: groundHeightAt(x, z, boxes, world.groundY, maxY, world.terrain),
    z,
  });
  const atOpen = (x: number, z: number): Vec3 => at(x, z); // dunes, exterior, dorsal ridge, knoll
  const atDeck0 = (x: number, z: number): Vec3 => at(x, z, 0.5); // Deck 0 floor
  const atCatwalk = (x: number, z: number): Vec3 => at(x, z, 4.5); // Deck 0 catwalks (y=4)
  const atChain = (x: number, z: number): Vec3 => at(x, z, DECK1_BASE + 0.5); // chain/stair/crest steps
  const atDeck1 = (x: number, z: number): Vec3 => at(x, z, DECK1_BASE + 0.5); // Deck 1 floor
  const atShaft = (x: number, z: number): Vec3 => at(x, z, DECK2_BASE + 0.5); // shaft ledges
  const atDeck2 = (x: number, z: number): Vec3 => at(x, z, DECK2_BASE + 0.5); // Deck 2 floor

  // ---------------------------------------------------------------------------------------------
  // Containers (44: 16 crate_common, 20 crate_hull, 8 crate_hymn)
  // ---------------------------------------------------------------------------------------------

  const containers: LootContainerDef[] = [];
  let nextContainerId = 1;
  const addContainer = (
    x: number,
    z: number,
    table: string,
    place: (x: number, z: number) => Vec3,
  ) => {
    containers.push({ id: nextContainerId++, pos: place(x, z), table });
  };

  // Deck 0 floor (16: 10 crate_hull, 6 crate_common), clear of the black-drop pit and of the
  // Deck 0 -> Deck 1 chain/stair risers (x -24..-16, which carry boxes well above y=0 at points
  // along their run - see CHAIN_RECT/STAIR_RECT above).
  for (const [x, z] of [
    [-14, 9],
    [-14, 15],
    [-14, 25],
    [-14, 31],
    [22, 9],
    [22, 15],
    [22, 25],
    [22, 31],
    [-10, 9],
    [10, 9],
  ] as const)
    addContainer(x, z, 'crate_hull', atDeck0);
  for (const [x, z] of [
    [-10, 31],
    [10, 31],
    [-14, 20],
    [18, 20],
    [0, 9],
    [0, 31],
  ] as const)
    addContainer(x, z, 'crate_common', atDeck0);

  // Deck 1 floor (10 crate_hull), clear of the chain/stair/shaft holes and the partitions.
  for (const [x, z] of [
    [-14, 10],
    [-6, 10],
    [2, 10],
    [10, 10],
    [14, 20],
    [-14, 26],
    [-6, 20],
    [6, 26],
    [-14, 33],
    [10, 33],
  ] as const)
    addContainer(x, z, 'crate_hull', atDeck1);

  // Deck 2 floor (8 crate_hymn), clear of pillars, pads, the shaft and the ridge chain.
  for (const [x, z] of [
    [-8, 12],
    [8, 12],
    [-20, 12],
    [20, 12],
    [-8, 30],
    [8, 30],
    [-20, 30],
    [0, 30],
  ] as const)
    addContainer(x, z, 'crate_hymn', atDeck2);

  // Dunes/scree (10 crate_common), scattered around the map.
  for (const [x, z] of [
    [-50, 50],
    [-30, 55],
    [0, 60],
    [30, 55],
    [50, 45],
    [-70, 20],
    [70, -20],
    [-40, -40],
    [40, -40],
    [-60, 60],
  ] as const)
    addContainer(x, z, 'crate_common', atOpen);

  // ---------------------------------------------------------------------------------------------
  // Extracts
  // ---------------------------------------------------------------------------------------------

  const extracts: ExtractDef[] = [
    { id: 1, name: 'Rail Car', pos: atOpen(0, -62), radius: 3 }, // terrain ridge crest
    { id: 2, name: 'Cargo Lift', pos: atDeck0(-18, 9), radius: 3 }, // Deck 0, north alcove
    { id: 3, name: 'Ferry Pickup', pos: atOpen(78, 15), radius: 3 }, // Antenna knoll, east
  ];

  // ---------------------------------------------------------------------------------------------
  // Quest areas (Expedition PURGE / HOLD / placed HUNT)
  // ---------------------------------------------------------------------------------------------

  // Cylinders, not boxes: `radius` is XZ, `halfHeight` the reach either side of `pos.y`. The hull is
  // 50 x 28 m at (0, 20), so a 28 m radius covers a deck end to end without spilling far outside;
  // the vertical bands are tuned to the 6-8 m deck pitch so two stacked decks stay distinct.
  const areas: AreaDef[] = [
    {
      id: 1,
      name: 'the Cargo Hold',
      pos: { x: 0, y: DECK0_BASE + 1, z: HULL_CZ },
      radius: 28,
      halfHeight: 6,
    },
    {
      id: 2,
      name: 'the Habitation Ring',
      pos: { x: 0, y: DECK1_BASE + 0.5, z: HULL_CZ },
      radius: 28,
      halfHeight: 2.9,
    },
    {
      id: 3,
      name: 'the Choir Deck',
      pos: { x: 0, y: DECK2_BASE + 0.5, z: HULL_CZ },
      radius: 28,
      halfHeight: 3,
    },
    {
      id: 4,
      name: 'the Dorsal Ridge',
      pos: { x: 0, y: RIDGE_Y + 0.5, z: HULL_CZ },
      radius: 28,
      halfHeight: 3.5,
    },
    { id: 5, name: 'the Approach Dunes', pos: atOpen(0, 56), radius: 30, halfHeight: 14 },
    { id: 6, name: 'the Antenna Knoll', pos: atOpen(74, 14), radius: 26, halfHeight: 14 },
  ];

  // ---------------------------------------------------------------------------------------------
  // The people standing on the Spine
  // ---------------------------------------------------------------------------------------------
  //
  // Four bodies for four of the six quest voices (see `quest-library.ts`). The Blender pass
  // (`tools/assetgen/blender/spine_region.py`) already modelled a marker slab, a lamp and a prop
  // at each of these four spots and exports them as `npcStands` in the sidecar; this table is the
  // authored fallback for a checkout that has never run Blender, and the source of everything the
  // sidecar does NOT carry - name, role, body, bark, voice.
  //
  // Only `x`/`z`/`yaw` are ever taken from the sidecar. `y` is derived here from the live terrain
  // and the live deck boxes, exactly like every other position in this file: a stand's y in the
  // JSON is what the sculpt happened to be at export time, and the sim walks *this* heightfield.
  // Ott is the reason that matters - he stands on the Choir Deck's floor slab, not on ground.
  //
  // `voice` is what makes them quest-givers rather than scenery: a finished authored rung spoken
  // by one of these four waits here to be handed in (see `expedition.ts`), while Ferryman Wren and
  // The Choir have no body on this map and keep auto-advancing.
  const npcStands: {
    id: string;
    name: string;
    role: string;
    body: string;
    voice: VoiceId;
    bark: string;
    bodyScale?: number;
    /** Authored fallback, overridden by the sidecar stand of the same id when there is one. */
    x: number;
    z: number;
    yaw: number;
    /** How this person's `y` is found: standing on the ground, or on a named deck's floor. */
    on: 'ground' | 'deck2';
  }[] = [
    {
      id: 'deacon_ott',
      name: 'Deacon Ott',
      role: 'The Choir Deck',
      // char-01 (vanguard): the tallest and the most armoured of the four. A Deacon who calls
      // killing "quieting" should read as somebody who has never once been in danger up here.
      body: 'char-01',
      voice: 'ott',
      bark: 'Stand there a moment and listen. The deck behind me is singing four parts tonight, and you are about to make it three.',
      x: 22.8,
      z: 10.2,
      yaw: 3.142,
      on: 'deck2',
    },
    {
      id: 'pell',
      name: 'Pell',
      role: 'the camp',
      // char-03 (mesh): the smallest silhouette in the library, scaled the rest of the way down.
      // Every rig is normalised to PLAYER_HEIGHT, so a nine-year-old has to be asked for.
      body: 'char-03',
      bodyScale: 0.78,
      voice: 'pell',
      bark: 'You are going in. Everyone goes in past me first. If you find a green toolroll, the initials are burned into the flap.',
      x: 12,
      z: 69.5,
      yaw: 0.3,
      on: 'ground',
    },
    {
      id: 'sabel_roque',
      name: 'Sabel Roque',
      role: 'Quartermaster',
      // char-02 (ch15): the most kitted of the four - webbing, pouches, a coat with places to
      // put a ledger. She weighs what comes off the rail car.
      body: 'char-02',
      voice: 'sabel',
      bark: 'The rail car leaves when it leaves. What you carry out gets weighed here; what you do not gets written down as shrinkage.',
      x: -5,
      z: -64,
      yaw: -1.571,
      on: 'ground',
    },
    {
      id: 'isolde_vane',
      name: 'Isolde Vane',
      role: 'Lanternfall',
      // char-04 (Erika Archer), the same body she has in the Yard: hooded, layered cloth over a
      // sash, and the only one in the library who does not read as kitted to go out there.
      body: 'char-04',
      voice: 'isolde',
      bark: 'The ferry holds this knoll until the dusk turns over. Come back to me before it does, and there will be nothing for me to write down.',
      x: 81.5,
      z: 20,
      yaw: 2.356,
      on: 'ground',
    },
  ];

  const sidecarStands = new Map(opts.region?.npcStands.map((s) => [s.id, s]) ?? []);
  const npcs: NpcDef[] = npcStands.map((stand, i) => {
    const placed = sidecarStands.get(stand.id);
    const x = placed?.pos.x ?? stand.x;
    const z = placed?.pos.z ?? stand.z;
    const npc: NpcDef = {
      // 1-based and stable in table order: the id is the only thing `InteractAction.Talk` carries.
      id: i + 1,
      name: stand.name,
      role: stand.role,
      body: stand.body,
      pos: stand.on === 'deck2' ? atDeck2(x, z) : atOpen(x, z),
      yaw: placed?.yaw ?? stand.yaw,
      bark: stand.bark,
      voice: stand.voice,
    };
    if (stand.bodyScale !== undefined) npc.bodyScale = stand.bodyScale;
    return npc;
  });

  // ---------------------------------------------------------------------------------------------
  // Waypoint graph (covers every deck; elevated nodes have y > 1.5)
  // ---------------------------------------------------------------------------------------------

  const nodes: Vec3[] = [];
  const edges: [number, number][] = [];
  const addNode = (x: number, z: number, place: (x: number, z: number) => Vec3): number => {
    nodes.push(place(x, z));
    return nodes.length - 1;
  };
  const addNodeXYZ = (p: Vec3): number => {
    nodes.push(p);
    return nodes.length - 1;
  };
  const chain = (ids: number[]) => {
    for (let i = 0; i < ids.length - 1; i++) edges.push([ids[i]!, ids[i + 1]!]);
  };
  const link = (a: number, b: number) => edges.push([a, b]);
  const grid = (xs: number[], zs: number[], place: (x: number, z: number) => Vec3): number[][] => {
    const rows: number[][] = zs.map((z) => xs.map((x) => addNode(x, z, place)));
    for (const row of rows) chain(row);
    for (let c = 0; c < xs.length; c++) chain(rows.map((row) => row[c]!));
    return rows;
  };

  // --- Deck 0 floor grid + catwalks + pit floor. ---------------------------------------------
  const deck0North = grid([-20, -12, -4, 4, 12, 20], [9], atDeck0)[0]!;
  const deck0South = grid([-20, -12, -4, 4, 12, 20], [30], atDeck0)[0]!;
  chain(deck0North);
  chain(deck0South);
  // x=-14 (not -22): clear of the chain/stair risers, which carry boxes well above y=0 at some
  // points along their x -24..-16 run.
  const deck0West = grid([-14], [9, 15, 20, 25, 30], atDeck0).flat();
  const deck0East = grid([22], [9, 15, 20, 25, 30], atDeck0).flat();
  link(deck0North[0]!, deck0West[0]!);
  link(deck0South[0]!, deck0West[4]!);
  link(deck0North[5]!, deck0East[0]!);
  link(deck0South[5]!, deck0East[4]!);
  // The two catwalks (y=4) sit 4.4 m apart over the pit with no floor between them, and are ~4 m
  // *above* the deck0 floor they'd need to be reached from - not mantleable (well past
  // MANTLE_MAX_HEIGHT) and not bridgeable by a walk. The pit floor (y=-6) is unreachable by falling
  // at all: the terrain heightfield's flat apron sits at y=0 across the *whole* hull footprint
  // (including under this hole), so stepPlayer's terrain-follow catches a fall at y=0 before it ever
  // reaches the real pit-floor box below - a reported "cuts through geometry" edge. None of these
  // three positions are needed for bot pathing (no extract or hymn patrol behind them), so they're
  // left as physical geometry for a human player to explore manually but pruned from the waypoint
  // graph entirely (see the offline traversal harness in spine.test.ts, which caught all of this).

  // --- Deck 0 -> Deck 1 chain + stair. ---------------------------------------------------------
  const chainIds = CHAIN.boxes.map((_, i) => {
    const b = CHAIN.boxes[i]!;
    return addNodeXYZ({ x: (b.min.x + b.max.x) / 2, y: b.max.y, z: (b.min.z + b.max.z) / 2 });
  });
  chain(chainIds);
  // The chain's treads stack side-by-side east of x=-24, rising with x (tread i's top climbs from
  // 1.1 m to 8 m as x runs -24..-16); a *diagonal* approach from deck0North[0] (x=-20, out on the
  // open floor) crosses straight through a taller mid-run riser's face (3-4+ m, well past
  // MANTLE_MAX_HEIGHT) before ever reaching tread 0 - exactly the "cuts through un-mantleable
  // geometry" bug reported by bots (confirmed by the offline traversal harness in spine.test.ts).
  // Two staging nodes fix it: a pure east-west leg at z=9 (north of every tread's z 10..16 span) into
  // the 0.8 m-wide clear corridor between the hull's west wall (interior face x=-24.8) and tread 0's
  // west face (x=-24.0), then a pure south leg down that corridor to z=13 (tread 0's z-centre), then
  // a short, purely +x approach straight into tread 0 - the same angle the (already-passing)
  // mantle-climb test below uses.
  const chainStageA = addNode(-24.4, 9, atDeck0);
  const chainStageB = addNode(-24.4, 13, atDeck0);
  link(deck0North[0]!, chainStageA);
  link(chainStageA, chainStageB);
  link(chainStageB, chainIds[0]!);
  // STAIR01's risers (0.4 m each, per the header/riser test below) are inside sim.ts's STEP_UP, so
  // a walker climbs the whole flight without a jump or a mantle. (Until 2026-09-04 stepPlayer had
  // no step-up at all and this stair was an impassable wall - a walker stalled dead at the first
  // riser - which is why it was, and still is, left out of the waypoint graph: the CHAIN above
  // already connects Deck 0 <-> Deck 1 and a second parallel route buys the bots nothing. It is a
  // real, walkable human route now rather than a decorative one.)

  // --- Deck 1 floor grid + exterior dune crest. -------------------------------------------------
  const deck1Row10 = grid([-14, -6, 2, 10], [10], atDeck1)[0]!;
  const deck1Row26 = grid([-14, -6, 2, 10], [26], atDeck1)[0]!;
  // Not grid()'s auto-chain: a direct z=33 link between these two would run straight through the
  // full-height (DECK1_H) berth partitions at x -10/-2/6 (z 27..33, see below) - a reported
  // "cuts through un-mantleable geometry" edge. Both ends already reach deck1Row26 (z=26, south of
  // the partitions), so no direct link between them is needed.
  const deck1Row33 = [addNode(-14, 33, atDeck1), addNode(10, 33, atDeck1)];
  chain(deck1Row10);
  chain(deck1Row26);
  const deck1East = [addNode(14, 20, atDeck1), addNode(20, 10, atDeck1), addNode(20, 30, atDeck1)];
  link(deck1Row10[3]!, deck1East[1]!);
  link(deck1East[1]!, deck1East[0]!);
  link(deck1East[0]!, deck1East[2]!);
  link(deck1East[2]!, deck1Row26[3]!);
  link(deck1Row10[0]!, deck1Row26[0]!);
  link(deck1Row26[0]!, deck1Row33[0]!);
  link(deck1Row26[3]!, deck1Row33[1]!);
  link(chainIds[chainIds.length - 1]!, deck1Row10[0]!);

  const creastIds = DUNE_CREST.boxes.map((b) => {
    const cx = (b.min.x + b.max.x) / 2;
    const y = b.max.y;
    // Below Deck 0's wall height, a step centred too close to the wall (x 24.8..25.2, the full
    // height of Deck 0's solid east wall there) has a node whose capsule clips it; pull those back.
    // The final step (y === DECK0_H, flush with the wall's top) doesn't need this.
    const x = y < DECK0_H ? Math.max(cx, HULL_X1 + WALL_T / 2 + PLAYER_RADIUS + 0.05) : cx;
    return addNodeXYZ({ x, y, z: (b.min.z + b.max.z) / 2 });
  });
  chain(creastIds);
  link(creastIds[creastIds.length - 1]!, deck1East[1]!);

  // --- Deck 1 -> Deck 2 shaft ledges. The open-drop half (SHAFT_RECT z 20..24) has no floor at
  // any deck by design (see SHAFT_RECT), so it gets no waypoint node - there's nothing to stand on.
  const shaftLedgeIds = SHAFT_LEDGES.boxes.map((b) =>
    addNodeXYZ({ x: (b.min.x + b.max.x) / 2, y: b.max.y, z: (b.min.z + b.max.z) / 2 }),
  );
  chain(shaftLedgeIds);
  link(deck1East[0]!, shaftLedgeIds[0]!);

  // --- Deck 2 floor grid + pillars zone + Verger arena centre. -----------------------------------
  // Not grid()'s auto-chain for row12: the x -8..8 span at z=12 runs straight through the internal
  // ridge stair (x -3..3, z 6..17.2, RIDGE_STAIR below), crossing a mid-run tread face metres
  // above the floor - a reported "cuts through un-mantleable geometry" edge. Keep the outer two
  // links (which stay clear of the stair's x span) and route around the middle one via deck2Row20
  // instead (added below, once that row exists).
  const deck2Row12 = [
    addNode(-20, 12, atDeck2),
    addNode(-8, 12, atDeck2),
    addNode(8, 12, atDeck2),
    addNode(20, 12, atDeck2),
  ];
  link(deck2Row12[0]!, deck2Row12[1]!);
  link(deck2Row12[2]!, deck2Row12[3]!);
  const deck2Row20 = grid([-20, -8, 0, 8], [20], atDeck2)[0]!;
  const deck2Row30 = grid([-20, -8, 0, 8, 20], [30], atDeck2)[0]!;
  chain(deck2Row20);
  chain(deck2Row30);
  link(deck2Row12[0]!, deck2Row20[0]!);
  link(deck2Row20[0]!, deck2Row30[0]!);
  // Both x=-8 and x=8, straight along z: stays clear of the ridge stair's x -3..3 span throughout.
  link(deck2Row12[1]!, deck2Row20[1]!);
  link(deck2Row12[2]!, deck2Row20[3]!);
  // deck2Row12[3] (x=20, z=12) <-> deck2Row30[4] (x=20, z=30) used to be a direct diagonal "shortcut"
  // - but a straight line between them runs through SHAFT_RECT (x 16..24, z 16..24), an open floor
  // hole with nothing below down to Deck 0 (see SHAFT_RECT above): a reported "cuts through
  // un-mantleable geometry" edge (really a straight fall). Removed; deck2Row30[4] still reaches the
  // rest of the graph via its own row chain -> deck2Row30[0] -> deck2Row20[0].
  const vergerCentre = addNodeXYZ({ ...SPINE_VERGER_SPAWN });
  link(deck2Row20[2]!, vergerCentre);
  link(shaftLedgeIds[shaftLedgeIds.length - 1]!, deck2Row12[3]!);

  // --- Internal ridge stair (arena floor -> roof) + dorsal ridge -> terrain ridge. -----------------
  const ridgeStairIds = RIDGE_STAIR.boxes.map((b) =>
    addNodeXYZ({ x: (b.min.x + b.max.x) / 2, y: b.max.y, z: (b.min.z + b.max.z) / 2 }),
  );
  chain(ridgeStairIds);
  // Approach the stair foot on a pure -z leg along x=0, never a diagonal from deck2Row12: the
  // flight now runs z 6..18.8, so any line coming in from the side crosses a tread *face* several
  // metres up (see the row12 note above). deck2Row20's x=0 node is straight south of the foot.
  link(deck2Row20[2]!, ridgeStairIds[0]!);
  // The stair's last tread and the dorsal ridge's hull-side end both sit exactly at y=RIDGE_Y,
  // flush with the solid roof between/around them (the hatch, then open sky over the wall, then
  // the exterior ridge) - a direct link is a real, obstruction-free walk at roof height.
  const dorsalIds = DORSAL_RIDGE.boxes.map((b) =>
    addNodeXYZ({ x: (b.min.x + b.max.x) / 2, y: b.max.y, z: (b.min.z + b.max.z) / 2 }),
  );
  chain(dorsalIds); // authored north-to-south: index 0 nearest the terrain ridge, last nearest the hull
  link(ridgeStairIds[ridgeStairIds.length - 1]!, dorsalIds[dorsalIds.length - 1]!);
  const railCarNode = addNode(0, -62, atOpen);
  link(dorsalIds[0]!, railCarNode);

  // --- Antenna knoll (Ferry Pickup). ----------------------------------------------------------
  const knollRows = grid([70, 78, 86, 94], [8, 15, 22], atOpen);
  const knollIds = knollRows.flat();

  // --- Dunes/scree grid + perimeter spawn clusters. ---------------------------------------------
  const dunesRows = grid([-100, -60, -20, 20, 60, 100], [45, 60], atOpen);
  const dunesIds = dunesRows.flat();
  const westRow = grid([-100, -100], [-30, 10], atOpen).flat();
  const eastRidgeApproach = grid([50, 90], [-30, -55], atOpen).flat();

  const spawnClusters: [number, number][][] = [
    [
      [-90, 60],
      [-84, 60],
      [-87, 55],
    ],
    [
      [95, 60],
      [90, 55],
      [93, 65],
    ],
    [
      [-90, -70],
      [-84, -70],
      [-87, -65],
    ],
    [
      [95, -70],
      [90, -65],
      [93, -75],
    ],
  ];
  const spawns: Vec3[] = spawnClusters.flat().map(([x, z]) => atOpen(x, z));

  const swHub = addNode(-87, 55, atOpen);
  link(swHub, dunesRows[1]![0]!);
  const seHub = addNode(93, 60, atOpen);
  link(seHub, dunesRows[1]![5]!);
  const nwHub = addNode(-87, -70, atOpen);
  link(nwHub, westRow[1]!);
  link(westRow[0]!, westRow[1]!);
  const neHub = addNode(93, -70, atOpen);
  link(neHub, eastRidgeApproach[1]!);
  link(eastRidgeApproach[0]!, eastRidgeApproach[1]!);
  // Used to link eastRidgeApproach[0] directly to dorsalIds[1] (a mid-run dorsal-ridge tread): a
  // straight line from open ground 10+ m below cuts diagonally across dorsalIds[2]'s much-taller
  // tread face first (each dorsal tread is only mantleable from the *previous* tread, not from raw
  // ground - a reported "cuts through un-mantleable geometry" edge). The dorsal ridge/Rail Car stay
  // reachable via the interior route (hull -> ridge chain -> dorsalIds[last..0] -> railCarNode).

  // Bridges the south dune grid to the south breach, and the west/east flanks into the dune grid.
  const southBridge = addNode(0, 40, atOpen);
  // A node centred in the 5 m-wide south breach (x -2.5..2.5 at z~34): the direct southBridge ->
  // deck0South[2] diagonal used to graze the breach's wall edge (a reported "cuts through
  // un-mantleable geometry" edge) since its straight line crossed the opening 0.35 m off-centre.
  const southBreach = addNode(0, 34.5, atOpen);
  link(southBridge, southBreach);
  link(southBreach, deck0South[2]!);
  link(dunesRows[0]![2]!, southBridge);
  // A single ~75 m edge from the dune grid down to the west/east ridge-flank hubs used to sprint the
  // whole way: SPRINT_DRAIN_PER_S (10/s) empties MAX_STAMINA (100) at 10 s, and a winded player can't
  // re-sprint (see sim.ts) - so it fell just short of the target inside the harness's 12 s budget.
  // Real bots hold Sprint the same way and would hit the same wall on a sustained corridor this long,
  // just recovering more slowly rather than getting stuck; breaking each into two shorter hops (one
  // waypoint stop roughly mid-corridor) keeps every individual hop well inside the stamina budget.
  const westMid = addNode(-100, 7, atOpen);
  link(dunesRows[0]![0]!, westMid);
  link(westMid, westRow[1]!);
  const eastMid = addNode(95, 7, atOpen);
  link(dunesRows[0]![5]!, eastMid);
  link(eastMid, eastRidgeApproach[1]!);
  link(eastRidgeApproach[1]!, knollIds[0]!);

  const waypoints: WaypointGraph = { nodes, edges };

  // ---------------------------------------------------------------------------------------------
  // Scree/knoll cover, filtered clear of every waypoint edge's straight-line path (deferred from
  // where SCREE_COVER/ANTENNA_COVER were generated above, until the graph exists to filter against).
  // A handful of the exterior dune/knoll edges are long, dead-straight lines over open terrain; with
  // scatter()'s seeded-random placement scattering cover across nearly the whole map, a prop could
  // (and, per the offline traversal harness below, did) land squarely on one of those lines - even a
  // short prop is a hard block, since stepPlayer's per-axis collision has no auto step-up and this
  // harness's walk/mantle policy only jumps for a rise > 0.5 m *toward the final target*, not for an
  // incidental obstacle in between. Dropping just the offending props (not the edges/nodes) keeps the
  // cover everywhere else intact.
  // ---------------------------------------------------------------------------------------------

  /** True if the 2D segment (ax,az)-(bx,bz) passes within `margin` of `box`'s XZ footprint
   *  (Liang-Barsky segment/rect clipping). */
  function segmentNearBoxXZ(
    ax: number,
    az: number,
    bx: number,
    bz: number,
    box: Aabb,
    margin: number,
  ): boolean {
    const x0 = box.min.x - margin;
    const x1 = box.max.x + margin;
    const z0 = box.min.z - margin;
    const z1 = box.max.z + margin;
    const dx = bx - ax;
    const dz = bz - az;
    let t0 = 0;
    let t1 = 1;
    const clip = (p: number, q: number): boolean => {
      if (p === 0) return q >= 0;
      const r = q / p;
      if (p < 0) {
        if (r > t1) return false;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return false;
        if (r < t1) t1 = r;
      }
      return true;
    };
    return (
      clip(-dx, ax - x0) && clip(dx, x1 - ax) && clip(-dz, az - z0) && clip(dz, z1 - az) && t0 <= t1
    );
  }

  const CLUTTER_EDGE_MARGIN = PLAYER_RADIUS + 0.15;
  /**
   * Height-aware: the decks stack on one footprint, so a Deck 0 corridor runs directly under
   * every Deck 1/2 prop. An edge only matters to a prop whose vertical extent overlaps the
   * band a walker on that edge occupies - the edge's own y span, a little below (a step down)
   * to PLAYER_HEIGHT-ish above it. The Blender pass (spine_wreck.py `clear3d`) uses this same
   * rule against the same graph, which is why its interior colliders survive this filter.
   */
  function clearOfEveryEdge(prop: Aabb): boolean {
    for (const [a, b] of edges) {
      const A = nodes[a]!;
      const B = nodes[b]!;
      const lo = Math.min(A.y, B.y) - 0.3;
      const hi = Math.max(A.y, B.y) + 2.0;
      if (prop.max.y < lo || prop.min.y > hi) continue;
      if (segmentNearBoxXZ(A.x, A.z, B.x, B.z, prop, CLUTTER_EDGE_MARGIN)) return false;
    }
    return true;
  }
  // With a region sidecar, its landmark colliders replace the in-code scree entirely (the Blender
  // script scatters its own rocks, with the same corridor keep-out); the filter stays on as a safety
  // net, and spine.test.ts asserts it never actually drops one - a dropped collider would be a rock
  // the client draws but the sim walks through.
  const regionBoxes: Aabb[] | undefined = opts.region
    ? opts.region.boxes.filter(clearOfEveryEdge)
    : undefined;
  if (regionBoxes) boxes.push(...regionBoxes);
  else
    boxes.push(...SCREE_COVER.filter(clearOfEveryEdge), ...ANTENNA_COVER.filter(clearOfEveryEdge));

  // ---------------------------------------------------------------------------------------------
  // Hymn spawns (18: 10 Chorister, 5 Cantor, 3 Sexton)
  // ---------------------------------------------------------------------------------------------

  // A fresh copy of a node's position - hymn spawns must not alias `nodes[i]`, since `SPINE` is a
  // module-level singleton shared by every room and a server mutating an entity's position in place
  // would otherwise silently corrupt the waypoint graph.
  const nodePos = (id: number): Vec3 => ({ ...nodes[id]! });

  const hymnSpawns: HymnSpawnDef[] = [
    // Chorister (10): dunes, Deck 0, Deck 1.
    // Spawn nudged from dunesIds[0] (-100,45), which sat 16.4 m from the SW spawn cluster — well
    // inside camping range of a fresh spawn (M4 owner-playtest report) — to dunesIds[1] (-60,45),
    // ~28.8 m out. Patrol left as-authored: it still walks through dunesIds[0] on its route.
    {
      kind: EntityKind.Chorister,
      pos: nodePos(dunesIds[1]!),
      patrol: [dunesIds[0]!, dunesIds[1]!, dunesIds[6]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(dunesIds[4]!),
      patrol: [dunesIds[4]!, dunesIds[5]!, dunesIds[10]!],
    },
    // Spawn nudged from dunesIds[11] (100,60), which sat 5 m from the SE spawn cluster — a Chorister
    // effectively spawned on top of it (M4 owner-playtest report) — to dunesIds[10] (60,60), ~30.4 m
    // out. Patrol left as-authored: it still walks through dunesIds[11] on its route.
    {
      kind: EntityKind.Chorister,
      pos: nodePos(dunesIds[10]!),
      patrol: [dunesIds[11]!, dunesIds[6]!, dunesIds[7]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck0North[1]!),
      patrol: [deck0North[1]!, deck0North[2]!, deck0South[2]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck0East[2]!),
      patrol: [deck0East[1]!, deck0East[2]!, deck0East[3]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck0West[2]!),
      patrol: [deck0West[1]!, deck0West[2]!, deck0West[3]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck1Row10[1]!),
      patrol: [deck1Row10[0]!, deck1Row10[1]!, deck1Row10[2]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck1Row26[2]!),
      patrol: [deck1Row26[1]!, deck1Row26[2]!, deck1Row26[3]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck1Row33[0]!),
      patrol: [deck1Row33[0]!, deck1Row33[1]!],
    },
    {
      kind: EntityKind.Chorister,
      pos: nodePos(deck1East[2]!),
      patrol: [deck1East[0]!, deck1East[2]!],
    },

    // Cantor (5): dune crest ledge, dorsal ridge steps, ridge crest overlook, the knoll.
    { kind: EntityKind.Cantor, pos: nodePos(creastIds[4]!), patrol: [] },
    {
      kind: EntityKind.Cantor,
      pos: nodePos(dorsalIds[1]!),
      patrol: [dorsalIds[1]!, dorsalIds[2]!],
    },
    { kind: EntityKind.Cantor, pos: nodePos(dorsalIds[3]!), patrol: [] },
    { kind: EntityKind.Cantor, pos: nodePos(railCarNode), patrol: [] },
    { kind: EntityKind.Cantor, pos: nodePos(knollIds[7]!), patrol: [knollIds[4]!, knollIds[7]!] },

    // Sexton (3): the hold (2) and the Choir Deck (1).
    { kind: EntityKind.Sexton, pos: nodePos(deck0North[3]!), patrol: [] },
    // Pit floor: no longer a waypoint node (see the "black-drop pit" comment above), so placed
    // directly at its position instead of via nodePos()/a node index.
    { kind: EntityKind.Sexton, pos: { x: 0, y: PIT_FLOOR_Y, z: 20 }, patrol: [] },
    { kind: EntityKind.Sexton, pos: nodePos(deck2Row20[1]!), patrol: [] },
  ];

  // ---------------------------------------------------------------------------------------------

  const map: MapDef = {
    id: 'spine',
    name: 'The Spine',
    world,
    spawns,
    containers,
    extracts,
    hymnSpawns,
    areas,
    npcs,
    waypoints,
    raidDurationMs: 20 * 60_000,
  };
  if (regionBoxes) map.regionBoxes = regionBoxes;
  if (opts.region && opts.region.dressedBoxes.length > 0) {
    // Which live kit boxes the wreck GLB dressed: matched by coordinates (cm), so a box that
    // spine.ts has since moved or added falls out of the list and keeps its greybox.
    const key = (b: Aabb): string =>
      [b.min.x, b.min.y, b.min.z, b.max.x, b.max.y, b.max.z]
        .map((v) => Math.round(v * 100))
        .join(',');
    const dressed = new Set(opts.region.dressedBoxes.map(key));
    const regionSet = new Set(regionBoxes ?? []);
    map.wreckDressedBoxes = boxes.filter((b) => !regionSet.has(b) && dressed.has(key(b)));
  }
  return map;
}

/**
 * The Spine. A singleton: `MAPS`, every room, the bots' nav cache and the client all hold this
 * object, so `applySpineRegion` rebuilds it *in place* rather than replacing it.
 */
export const SPINE: MapDef = buildSpine();

/**
 * Re-authors `SPINE` against a region sidecar (or back to the in-code fallback with `null`).
 * Call it once at startup, before any room or prediction exists - a room that captured
 * `SPINE.world` earlier keeps colliding against the old terrain.
 */
export function applySpineRegion(region: SpineRegion | null): MapDef {
  const next = buildSpine(region ? { region } : {});
  // `regionBoxes` is the one optional key: drop it explicitly when reverting so the fallback map
  // doesn't keep a stale list from a previous apply.
  delete SPINE.regionBoxes;
  delete SPINE.wreckDressedBoxes;
  Object.assign(SPINE, next);
  return SPINE;
}
